// MarketingShell · universal page template used by all the secondary
// broker pages (markets, platforms, regulation, etc.). Renders a
// consistent hero + section grid wrapped in the Navbar + Footer chrome.
//
// The hero itself is delegated to <PageHero/> so every marketing page
// inherits the same big-centered look (with 4 brand-pillar icon cards
// at the bottom and seamless orb-blended background).
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarketTicker from "@/components/MarketTicker";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";
import { REGISTER_URL } from "@/data/site";

export default function MarketingShell({
  eyebrow,
  title,
  titleAccent,
  lead,
  primary,
  secondary,
  breadcrumb,
  testid = "marketing-page",
  showFeatures = true,
  artwork,
  children,
  footerCta = true,
}) {
  const { t } = useTranslation();
  // i18n default for the primary CTA. Computed inside the body so we
  // pick up the active locale on every render.
  const primaryWithFallback = primary || {
    label: t("common.openAccount", "Open Account"),
    href: REGISTER_URL,
    external: true,
  };
  return (
    <div data-testid={testid} className="relative overflow-x-hidden">
      <Navbar />

      <PageHero
        breadcrumb={breadcrumb}
        pill={eyebrow ? { text: eyebrow } : undefined}
        title={title}
        accent={titleAccent}
        subline={lead}
        primary={primaryWithFallback}
        secondary={secondary}
        showFeatures={showFeatures}
        artwork={artwork}
        testId={`${testid}-hero`}
      />

      {/* BODY */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">{children}</div>

      {/* FOOTER CTA · uses the global institutional CTABanner so every
          MarketingShell page (markets, platforms, account types, info)
          inherits the same compelling closer (live spreads · 4-step
          opening flow · trust signals) as the rest of the site. */}
      {footerCta && <CTABanner />}

      <MarketTicker />
      <Footer />
    </div>
  );
}

/* ============ REUSABLE BUILDING BLOCKS ============ */
// FeatureGrid · auto-picks the column count from `items.length` so we
// never end up with a lonely orphan card on a new row (the issue user
// flagged on /funding, /safety-of-funds, /leverage etc. which have 4
// perks each but were laid out in a 3-col grid → 4th card alone).
export function FeatureGrid({ items, cols, testid }) {
  const n = items.length;
  // 1, 2, 3, 4 → use exactly that many columns. 5 → 5 cols would be
  // too narrow, fall back to 3 (which orphans 2 — much better than 1).
  // 6+ → 3 cols (2 full rows).
  const lg = cols ?? (n <= 4 ? n : (n === 5 ? 5 : 3));
  const sm = cols ?? (n <= 2 ? n : 2);
  const lgCls = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5" }[lg] || "lg:grid-cols-3";
  const smCls = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2" }[sm] || "sm:grid-cols-2";
  return (
    <div className={`grid grid-cols-1 ${smCls} ${lgCls} gap-5 lg:gap-6`} data-testid={testid}>
      {items.map(({ Icon, title, body, accent }, i) => (
        <motion.div
          key={title}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl p-6 border bg-[color:var(--surface)] hover:border-line-strong transition-colors group"
          style={{ borderColor: "var(--line)" }}
        >
          {Icon && (
            <div
              className="mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3"
              style={{ color: accent || "var(--brand)" }}
            >
              <Icon size={28} strokeWidth={1.6} />
            </div>
          )}
          <h3 className="font-display text-lg font-medium text-text-1 mb-2 leading-tight">{title}</h3>
          <p className="text-text-3 text-[13.5px] leading-relaxed font-light">{body}</p>
        </motion.div>
      ))}
    </div>
  );
}

export function SectionHeading({ eyebrow, title, accent, lead, className = "" }) {
  return (
    <div className={`text-center mb-12 lg:mb-16 ${className}`}>
      {eyebrow && <div className="pill-mono inline-flex mb-4">{eyebrow}</div>}
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1">
        {title} {accent && <span className="text-gradient-brand font-semibold">{accent}</span>}
      </h2>
      {lead && <p className="text-text-3 mt-5 max-w-2xl mx-auto text-[15px] leading-relaxed">{lead}</p>}
    </div>
  );
}
